import { createSlice } from '@reduxjs/toolkit';
import type { Dispatch, PayloadAction } from '@reduxjs/toolkit';

/**
 * Operations the Telegram Groups admin API runs through the async job flow
 * (SKY-4445). `import` is intentionally excluded — it isn't wired into the
 * dashboard. `bulk-archive`/`bulk-delete` (SKY-4484) run a single job against
 * many groups at once — see `groupIds` below. `bulk-add-members`/
 * `bulk-remove-members` (SKY-4559) run a single job against a `userIds x
 * groupIds`/`scope` pair matrix — `groupIds` is absent for a `scope`-targeted
 * job (resolved server-side, never known to this client up front).
 */
export type TradingGroupJobOperation =
  | 'create'
  | 'update'
  | 'archive'
  | 'delete'
  | 'bulk-archive'
  | 'bulk-delete'
  | 'change-bot'
  | 'add-members'
  | 'remove-members'
  | 'bulk-add-members'
  | 'bulk-remove-members';

/**
 * Per-item progress for a job that reports it (only `import` today, and
 * `import` isn't wired into the dashboard yet — this exists for when it is).
 * Not necessarily monotonic: a retryable job can restart from 0, so consumers
 * must render the latest value rather than clamping to a maximum.
 */
export interface TradingGroupJobProgress {
  processed: number;
  total: number;
  /** Free-form label (e.g. the item just handled) — presentational only. */
  hint?: string;
}

export interface TradingGroupJobEntry {
  operation: TradingGroupJobOperation;
  /** Affected group id, known up front for update/archive/delete; absent for create until `JobCompleted`. */
  groupId?: number;
  /** Affected group ids, known up front for `bulk-archive`/`bulk-delete` — absent for a single-group operation. */
  groupIds?: number[];
  /** Latest progress report, when this job's kind reports one — absent otherwise. */
  progress?: TradingGroupJobProgress;
  /**
   * The bot username submitted for a `change-bot` job (SKY-4526) — absent for
   * every other operation. Carried through to the settled notification so a
   * failure's Retry action can resubmit the exact same bot (the worker
   * operation is idempotent, so retrying with it is the documented remedy for
   * `CHANGE_BOT_NOT_CONFIRMED`/`CHANGE_BOT_INVITE_FAILED`).
   */
  botUsername?: string;
  /**
   * The auth user ids submitted for an `add-members`/`remove-members` job
   * (SKY-4558) — absent for every other operation. Carried through purely for
   * descriptive purposes (e.g. "Adding 3 members…"); the per-row outcome
   * report itself is fetched fresh from `GET .../jobs/:jobId`, not derived
   * from this list.
   */
  userIds?: number[];
  /**
   * The new title submitted for an `update` job that renamed the group
   * (SKY-4546) — absent for a comment/accountType-only update, or any other
   * operation. Carried through to the settled notification so a rename
   * failure's Retry action can resubmit the exact same title (nothing is
   * persisted on failure, so retrying is always safe).
   */
  title?: string;
}

/** A settled job, queued for the notification UI to announce once and discard. */
export interface TradingGroupJobNotification {
  jobId: string;
  operation: TradingGroupJobOperation;
  groupId?: number;
  /** Affected group ids for a `bulk-archive`/`bulk-delete` notification — absent for a single-group operation. */
  groupIds?: number[];
  /** The bot username submitted for a `change-bot` job — see `TradingGroupJobEntry.botUsername`. */
  botUsername?: string;
  /** The auth user ids submitted for an `add-members`/`remove-members` job — see `TradingGroupJobEntry.userIds`. */
  userIds?: number[];
  /** The new title submitted for a rename job — see `TradingGroupJobEntry.title`. */
  title?: string;
  /**
   * `unknown` means the stale-job backstop settled this job because its
   * `JobCompleted`/`JobFailed` frame never arrived — the job's actual result
   * was never observed, so the notification UI must not claim success.
   */
  outcome: 'completed' | 'failed' | 'unknown';
  /** Present only when `outcome` is `failed`. */
  error?: string;
  /** Human-readable failure description, present when the backend provides one. */
  message?: string;
  /**
   * Stable failure code when known — the backend's **fully-qualified i18n
   * key**, e.g. `telegram-group.exceptions.FLOOD_WAIT`, never a bare
   * `FLOOD_WAIT`. Consumers must branch on the constants exported from
   * `@skynet/data-trading-groups` rather than inline literals: a mismatch fails
   * silently, falling through to the generic failure toast with no other
   * signal. Deliberately not listed here — this comment went stale the first
   * time a code was added; `bulk-result.ts` is the one place that enumerates
   * them, and `ARCHIVE_FORCE_CODES` / `isArchiveForceCode` cover the ones a
   * force-archive recovery keys on.
   */
  code?: string;
  /** Present alongside a `FLOOD_WAIT` `code`. */
  retryAfterSeconds?: number;
}

/**
 * Tracks Telegram Groups jobs currently in flight, keyed by the `jobId` a
 * mutation's `202` response returned. Entries are added optimistically as
 * soon as a mutation is accepted (before any WS frame arrives) and removed
 * once the assistant WS channel's `JobCompleted`/`JobFailed` frame settles the
 * job — see the dashboard's `ws-assistant-effects`. A jobId absent from this
 * map is either unknown to this client or already settled, so an incoming WS
 * frame for it is ignored: this is what keeps other users' broadcast jobs
 * from affecting this client's UI.
 */
export interface TradingGroupJobsState {
  byJobId: Record<string, TradingGroupJobEntry>;
  /** Settled jobs waiting to be announced — drained by the notification UI. */
  notifications: TradingGroupJobNotification[];
}

export const initialState: TradingGroupJobsState = {
  byJobId: {},
  notifications: [],
};

const tradingGroupJobsSlice = createSlice({
  name: 'tradingGroupJobs',
  initialState,
  reducers: {
    jobStarted(
      state,
      action: PayloadAction<{
        jobId: string;
        operation: TradingGroupJobOperation;
        groupId?: number;
        groupIds?: number[];
        botUsername?: string;
        userIds?: number[];
        title?: string;
      }>,
    ) {
      const { jobId, operation, groupId, groupIds, botUsername, userIds, title } = action.payload;
      state.byJobId[jobId] = { operation, groupId, groupIds, botUsername, userIds, title };
    },
    /**
     * Records a job's latest progress report. A no-op for a jobId this client
     * isn't tracking (unknown, already settled, or another user's job) — same
     * "untracked frame is ignored" rule as `jobSettled`. Overwrites rather than
     * merges: progress isn't monotonic (a retryable job can restart from 0),
     * so the previous value is never meaningful once a newer one arrives.
     */
    jobProgressed(
      state,
      action: PayloadAction<{ jobId: string; progress: TradingGroupJobProgress }>,
    ) {
      const { jobId, progress } = action.payload;
      const entry = state.byJobId[jobId];

      if (entry) {
        entry.progress = progress;
      }
    },
    /**
     * Settles a job, whatever its outcome. A no-op for a jobId this client
     * isn't tracking (unknown, already settled, or another user's broadcast
     * job) — nothing is queued for the notification UI in that case.
     * `groupId` overrides the entry's own (absent for `create` until
     * `JobCompleted` reports the created group's id via `resultId`).
     */
    jobSettled(
      state,
      action: PayloadAction<{
        jobId: string;
        outcome: 'completed' | 'failed' | 'unknown';
        groupId?: number;
        groupIds?: number[];
        error?: string;
        message?: string;
        code?: string;
        retryAfterSeconds?: number;
      }>,
    ) {
      const { jobId, outcome, groupId, groupIds, error, message, code, retryAfterSeconds } =
        action.payload;
      const entry = state.byJobId[jobId];
      delete state.byJobId[jobId];

      if (entry) {
        state.notifications.push({
          jobId,
          operation: entry.operation,
          groupId: groupId ?? entry.groupId,
          groupIds: groupIds ?? entry.groupIds,
          botUsername: entry.botUsername,
          userIds: entry.userIds,
          title: entry.title,
          outcome,
          error,
          message,
          code,
          retryAfterSeconds,
        });
      }
    },
    /**
     * Removes only the given jobIds' notifications — called with the batch the
     * notification UI just displayed. Clearing the whole queue unconditionally
     * would also discard any notification queued between that render and this
     * dispatch (e.g. a second job settling right after), before it was ever shown.
     */
    notificationsCleared(state, action: PayloadAction<string[]>) {
      const shownIds = new Set(action.payload);
      state.notifications = state.notifications.filter((n) => !shownIds.has(n.jobId));
    },
  },
});

export const tradingGroupJobsActions = tradingGroupJobsSlice.actions;

/** Any action created by {@link tradingGroupJobsActions}. */
export type TradingGroupJobsAction = ReturnType<
  (typeof tradingGroupJobsActions)[keyof typeof tradingGroupJobsActions]
>;

/**
 * A dispatch narrowed to this slice's actions, for consumers that only write
 * this slice (`useDispatch<TradingGroupJobsDispatch>()`). The app's root dispatch
 * satisfies it (checked in `store/package-store-contracts.test.ts`).
 */
export type TradingGroupJobsDispatch = Dispatch<TradingGroupJobsAction>;

export default tradingGroupJobsSlice.reducer;
